// Leaderboard — Public page showing top members and territories.
// Route: /leaderboard
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import * as Lucide from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const RED = "#C8102E";
const GOLD = "#D4AF37";
const CHARCOAL = "#1A1A1A";

const KINDS = [
  { key: "referrers",     label: "Top Referrers",     unit: "signups" },
  { key: "commissions",   label: "Top Earners",       unit: "RM" },
  { key: "leads",         label: "Most Enquiries",    unit: "leads" },
  { key: "territories",   label: "Top Opportunities", unit: "leads" },
];
const PERIODS = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "yearly", label: "Yearly" },
  { key: "alltime", label: "All-Time" },
];

function Medal({ index }) {
  if (index === 0) return <span className="text-2xl">🥇</span>;
  if (index === 1) return <span className="text-2xl">🥈</span>;
  if (index === 2) return <span className="text-2xl">🥉</span>;
  return <span className="font-display font-black text-lg text-zinc-500">#{index + 1}</span>;
}

export default function Leaderboard() {
  const [kind, setKind] = useState("referrers");
  const [period, setPeriod] = useState("monthly");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${BACKEND}/api/public/leaderboard?kind=${kind}&period=${period}`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [kind, period]);

  const currentKind = KINDS.find((k) => k.key === kind);

  return (
    <div className="min-h-screen bg-white text-[#09090b]">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold" data-testid="lb-back"><Lucide.ArrowLeft className="w-4 h-4" /> Home</Link>
          <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500">MIRESCO Leaderboard</div>
          <div className="w-10" />
        </div>
      </header>

      <section className="border-b border-zinc-200 text-white" style={{ background: CHARCOAL }}>
        <div className="max-w-6xl mx-auto px-5 lg:px-10 py-14 lg:py-20">
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold px-3 py-1.5 rounded-full mb-4" style={{ background: `${GOLD}30`, color: GOLD }}>
            <Lucide.Trophy className="w-3.5 h-3.5" /> Live leaderboard
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-[1.05]" data-testid="lb-title">
            Malaysia&apos;s Top <span style={{ color: GOLD }}>MIRESCO</span> Members
          </h1>
          <p className="mt-3 text-white/80 max-w-2xl">Real numbers. Real people. Real income. Your name could be here next month.</p>
        </div>
      </section>

      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 py-6 flex flex-wrap gap-3">
          <div className="flex flex-wrap gap-2" data-testid="lb-kind-tabs">
            {KINDS.map((k) => (
              <button key={k.key} onClick={() => setKind(k.key)} className={`text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border transition-colors ${kind === k.key ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`} data-testid={`lb-kind-${k.key}`}>
                {k.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 ml-auto" data-testid="lb-period-tabs">
            {PERIODS.map((p) => (
              <button key={p.key} onClick={() => setPeriod(p.key)} className={`text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border transition-colors ${period === p.key ? "bg-[#C8102E] text-white border-[#C8102E]" : "border-zinc-300 hover:border-zinc-900"}`} data-testid={`lb-period-${p.key}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 lg:px-10 py-10">
        {loading ? (
          <div className="font-mono text-sm text-zinc-500 py-12 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500" data-testid="lb-empty">
            No entries yet for {currentKind?.label} · {period}. Be the first to make it on the board!
          </div>
        ) : (
          <ol className="divide-y border-y border-zinc-200" data-testid="lb-list">
            {items.map((row, i) => (
              <li key={i} className="py-4 px-2 flex items-center gap-4" data-testid={`lb-row-${i}`}>
                <div className="w-10 text-center"><Medal index={i} /></div>
                <div className="flex-1 min-w-0 flex items-center gap-3">
                  {row.user ? (
                    <>
                      {row.user.picture ? (
                        <img src={row.user.picture} alt="" className="w-10 h-10 object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-zinc-100 grid place-items-center font-black text-zinc-500">{(row.user.name || "M")[0]}</div>
                      )}
                      <div className="min-w-0">
                        <div className="font-display font-black text-base tracking-tight truncate">{row.user.name || "MIRESCO Member"} {row.user.is_verified && <span title="Verified" className="text-emerald-600">✓</span>}</div>
                        <div className="text-xs text-zinc-500 truncate">{row.user.location || "Malaysia"}</div>
                      </div>
                    </>
                  ) : row.opportunity ? (
                    <>
                      <div className="w-10 h-10 bg-zinc-100 grid place-items-center font-black text-zinc-500 overflow-hidden">
                        {row.opportunity.hero_image_url ? <img src={row.opportunity.hero_image_url} alt="" className="w-full h-full object-cover" /> : "🏢"}
                      </div>
                      <div className="min-w-0">
                        <Link to={`/opportunity/${row.opportunity.slug}`} className="font-display font-black text-base tracking-tight truncate hover:underline">{row.opportunity.title}</Link>
                        <div className="text-xs text-zinc-500">{(row.opportunity.states || []).join(", ") || (row.opportunity.countries || []).join(", ") || "Malaysia"}</div>
                      </div>
                    </>
                  ) : null}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-black text-xl" style={{ color: i === 0 ? GOLD : RED }} data-testid={`lb-value-${i}`}>
                    {currentKind?.unit === "RM" ? `RM ${Number(row.value || 0).toLocaleString()}` : Number(row.value || 0).toLocaleString()}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{currentKind?.unit}</div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
